import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { flexbox, space } from 'styled-system';
import base64 from 'base-64';
import { useSelector, useDispatch } from 'react-redux';
import { getAggrRequest } from 'redux-api/action/aggr';
import selectTopBusiness from 'redux-api/reselect/top-business';
import { useOutsideClick } from 'hooks';
import Card from 'theme/card';
import List from 'theme/list';
import Chart from './chart';
import Row from './row';

const Wrapper = styled.div`
  ${space}
  ${flexbox}

  display: flex;
  width: 100%;
  max-width: 846px;
  flex-wrap: wrap;
`;

function BusinessRevenues({
  compare,
  dataType,
  dateTo,
  dateFrom,
  dateRange,
  filters,
  terms,
  reloadChecked,
  isAdmin,
}) {
  const [selectedPieItem, setSelectedPieItem] = useState(null);
  const [queriedKey, setQueriedKey] = useState(null);

  const ref = useRef();
  const dispatch = useDispatch();

  const pieAssets = useSelector((state) => selectTopBusiness(state, queriedKey));
  const { currency } = useSelector((state) => state.config);
  const { analytics_type } = useSelector((state) => state.profile.payload);

  const query = useMemo(() => {
    if (!dataType || !dateTo || !dateFrom) return null;

    let queryString = `currency=${currency}&dataType=${dataType}&term=order_type&size=10&dateTo=${dateTo}&dateFrom=${dateFrom}&dateRange=${dateRange}&compare=${compare}&version=7&enterprise=${
      analytics_type || 'standard'
    }&isAdmin=${isAdmin}`;

    if (filters) {
      queryString = `${queryString}&filters=${filters}`;
    }

    if (terms) {
      queryString = `${queryString}&terms=${terms}`;
    }

    return queryString;
  }, [
    dateTo,
    dateFrom,
    dataType,
    dateRange,
    filters,
    terms,
    currency,
    compare,
    analytics_type,
    isAdmin,
  ]);

  useEffect(() => {
    if (!query) return;

    const key = base64.encode(query);

    setQueriedKey(key);

    dispatch(
      getAggrRequest({
        term: 'order_type',
        key,
        query,
        currency,
        isAdmin,
      }),
    );
  }, [dispatch, dateTo, dateFrom, dataType, query, currency, reloadChecked, isAdmin]);

  const handleSelectRow = useCallback((id) => {
    setSelectedPieItem(id);
  }, []);

  useOutsideClick(ref, () => {
    setSelectedPieItem(null);
  });

  return (
    <Wrapper
      flexDirection={['column', 'column', 'column', 'row']}
      justifyContent="center"
      alignItems={['center', 'center', 'center', 'flex-start']}
      pt="l"
      px="l"
      pb="xl"
    >
      <Chart pieAssets={pieAssets} id={queriedKey} maxWidth="220" selected={selectedPieItem} />
      <Card
        maxWidth={['100%', '100%', '467px']}
        width="100%"
        bg="white"
        border="none"
        mt="m"
        ml={['0', '0', '0', 'm']}
      >
        <span ref={ref}>
          <List>
            {pieAssets.pieChartData.map((t) => (
              <Row
                key={t.label}
                payload={t}
                onClick={handleSelectRow}
                selected={t.color === selectedPieItem}
              />
            ))}
          </List>
        </span>
      </Card>
    </Wrapper>
  );
}

export default BusinessRevenues;
